import { ExpandMore } from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { fontSize } from '../..';
import { getScheduledNextRuns, getSchedulePastRuns } from '../../api';

interface PastNextRunProps {
  selectedSchedule: any;
  selectedDataset: any;
}

// const nextRuns = {
//   nextRunTime: '2025-11-20T08:30:00',
// };

// const pastRuns = [
//   {
//     startTime: '2025-11-18T15:30:00.001987',
//     endTime: '2025-11-18T15:30:00.079963',
//     status: 'SUCCESS',
//   },
//   {
//     startTime: '2025-11-18T15:00:00.00271',
//     endTime: '2025-11-18T15:00:00.098019',
//     status: 'FAILED',
//   },
//   {
//     startTime: '2025-11-18T14:30:00.002233',
//     endTime: '2025-11-18T14:30:00.102673',
//     status: 'RUNNING',
//   },
// ];

const PastNextRun = ({ selectedSchedule, selectedDataset }: PastNextRunProps) => {
  const [activeSection, setActiveSection] = useState<string | null>('nextRun');

  const [nextRuns, setNextRuns] = useState<any>([]);
  const [pastRuns, setPastRuns] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  const getNextAndPastRuns = async () => {
    if (!selectedSchedule) return;
    if (!selectedDataset || !selectedDataset?.workspaceId || !selectedDataset?.datasetId) return;
    setLoading(true);
    const nextRuns = await getScheduledNextRuns(
      selectedDataset.datasetId,
      selectedDataset.workspaceId,
      selectedSchedule.id,
    );
    const pastRuns = await getSchedulePastRuns(
      selectedDataset.datasetId,
      selectedDataset.workspaceId,
      selectedSchedule.id,
    );
    setNextRuns(nextRuns.data);
    setPastRuns(pastRuns.data);
    setLoading(false);
  };

  useEffect(() => {
    if (selectedSchedule) {
      getNextAndPastRuns();
    }
  }, [selectedSchedule]);

  if (!selectedSchedule) {
    return null;
  }

  return (
    <Box mt={2}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height={150}>
          <CircularProgress size={30} sx={{ color: '#2bb9bb' }} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              px: 0,
            }}
          >
            <Accordion
              elevation={0}
              sx={{
                boxShadow: 'none',
                '&:before': { display: 'none' },
                background: 'transparent',
              }}
              expanded={activeSection === 'nextRun'}
              onChange={(e, expanded) => {
                e.stopPropagation();
                setActiveSection(expanded ? 'nextRun' : null);
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  px: 0,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>Next Run</Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: 1,
                      borderBottom: '1px solid #e0e0e0',
                      mx: 2,
                      alignSelf: 'center',
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{ p: 0, mt: -1, px: 1.5 }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {nextRuns?.nextRunTime ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Scheduled for:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(nextRuns.nextRunTime).toLocaleString()}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No upcoming run scheduled.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>

          <Box
            sx={{
              mb: 1,
              px: 0,
            }}
          >
            <Accordion
              elevation={0}
              sx={{
                boxShadow: 'none',
                '&:before': { display: 'none' },
                background: 'transparent',
              }}
              expanded={activeSection === 'pastRuns'}
              onChange={(e, expanded) => {
                e.stopPropagation();
                setActiveSection(expanded ? 'pastRuns' : null);
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  px: 0,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>Past Runs</Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: 1,
                      borderBottom: '1px solid #e0e0e0',
                      mx: 2,
                      alignSelf: 'center',
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{ px: 0, pt: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <TableContainer>
                  {pastRuns.length === 0 && (
                    <Box display="flex" justifyContent="center" alignItems="center" height={50}>
                      <Typography variant="body1" color="textSecondary">
                        No past runs found. The dataset has not been run yet.
                      </Typography>
                    </Box>
                  )}

                  {pastRuns && pastRuns.length > 0 && (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Start Time</strong>
                          </TableCell>
                          <TableCell>
                            <strong>End Time</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Status</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pastRuns &&
                          pastRuns.length > 0 &&
                          pastRuns.map((run: any, idx: number) => (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                  {new Date(run.startTime).toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                  {new Date(run.endTime).toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '8px',
                                    backgroundColor:
                                      run.status === 'SUCCESS'
                                        ? '#e6f4ea'
                                        : run.status === 'FAILED'
                                          ? '#fdeaea'
                                          : '#eaffffff',
                                    color:
                                      run.status === 'SUCCESS'
                                        ? '#388e3c'
                                        : run.status === 'FAILED'
                                          ? '#d32f2f'
                                          : '#2bb9bb',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {run.status.toLowerCase()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PastNextRun;
